import React from "react";
import { createPortal } from "react-dom";
import { autoUpdate, flip, offset, shift, useFloating } from "@floating-ui/react-dom";
import { LoaderCircle, Palette, RotateCcw } from "lucide-react";
import usePopoverDismiss from "../hooks/usePopoverDismiss.js";
import { useUserUiPreferences } from "../hooks/useUserUiPreferences.js";
import {
  DEFAULT_DISPATCH_COLOR_INTENSITY_PERCENT,
  DISPATCH_COLOR_INTENSITY_MAX,
  DISPATCH_COLOR_INTENSITY_MIN,
} from "../utils/userUiPreferences.js";
import { getReactPortalRoot } from "../utils/reactPortalRoot.js";
import { joinClasses } from "./ResultsPrimitives.jsx";

function formatSignedPercent(value) {
  return `${value > 0 ? "+" : ""}${value} %`;
}

function settlePreferenceOperation(operation) {
  try {
    const result = operation?.();
    if (result && typeof result.catch === "function") {
      void result.catch(() => {});
    }
  } catch {}
}

export default function DispatchColorIntensityControl({ menuItem = false, parentOpen = true }) {
  const anchorRef = React.useRef(null);
  const sliderRef = React.useRef(null);
  const popoverId = React.useId();
  const sliderId = React.useId();
  const [isOpen, setIsOpen] = React.useState(false);
  const { refs, floatingStyles, isPositioned } = useFloating({
    open: isOpen,
    placement: "bottom-end",
    middleware: [
      offset(7),
      flip({ padding: 16 }),
      shift({ padding: 16 }),
    ],
    whileElementsMounted: autoUpdate,
  });
  const {
    dispatchColorIntensityPercent,
    errorMessage,
    flush,
    hasPendingChanges,
    isLoading,
    isReady,
    isSaving,
    resetDispatchColorIntensity,
    setDispatchColorIntensityPercent,
    userId,
  } = useUserUiPreferences();
  const flushRef = React.useRef(flush);
  flushRef.current = flush;

  React.useEffect(() => () => {
    settlePreferenceOperation(() => {
      return flushRef.current?.();
    });
  }, []);

  const closePopover = React.useCallback(() => {
    setIsOpen(false);
  }, []);

  React.useEffect(() => {
    if (!parentOpen) closePopover();
  }, [closePopover, parentOpen]);

  React.useEffect(() => {
    refs.setReference(anchorRef.current);
  }, [isOpen, refs]);

  usePopoverDismiss({
    anchorElement: anchorRef.current,
    floatingRef: refs.floating,
    isOpen,
    onClose: closePopover,
  });

  const handlePopoverKeyDown = React.useCallback((event) => {
    if (event.key !== "Escape") {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    closePopover();
    anchorRef.current?.querySelector('[data-dispatch-color-intensity-trigger="true"]')?.focus();
  }, [closePopover]);

  React.useEffect(() => {
    if (!isOpen || typeof window === "undefined") {
      return undefined;
    }

    const frameId = window.requestAnimationFrame(() => {
      sliderRef.current?.focus();
    });
    return () => {
      window.cancelAnimationFrame(frameId);
    };
  }, [isOpen]);

  const handleSliderChange = React.useCallback((event) => {
    setDispatchColorIntensityPercent(Number(event.target.value));
  }, [setDispatchColorIntensityPercent]);

  const handleReset = React.useCallback(() => {
    settlePreferenceOperation(() => {
      return resetDispatchColorIntensity({ immediate: true });
    });
    sliderRef.current?.focus();
  }, [resetDispatchColorIntensity]);

  const formattedValue = formatSignedPercent(dispatchColorIntensityPercent);
  const triggerDisabled = !userId;
  const statusLabel = isSaving
    ? "Enregistrement…"
    : hasPendingChanges
      ? "Aperçu en cours…"
      : isLoading
        ? "Chargement…"
        : isReady
          ? "Synchronisé"
          : "Disponible";

  return (
    <div
      ref={anchorRef}
      className="relative inline-flex shrink-0"
      data-dispatch-color-intensity-control="true"
    >
      <button
        type="button"
        className={joinClasses([
          "inline-flex h-[30px] min-h-[30px] shrink-0 items-center justify-center gap-1.5 whitespace-nowrap rounded-[4px] border border-slate-200 bg-white px-[0.7rem] text-[11px] font-extrabold text-slate-600 shadow-none transition-colors duration-150",
          "hover:bg-slate-100 hover:text-slate-950 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2",
          triggerDisabled ? "cursor-wait opacity-60" : "",
          menuItem ? "dispatch-header-menu-action" : "",
        ])}
        data-dispatch-color-intensity-trigger="true"
        data-keep-menu-open={menuItem || undefined}
        role={menuItem ? "menuitem" : undefined}
        tabIndex={menuItem ? -1 : undefined}
        aria-controls={popoverId}
        aria-expanded={isOpen}
        aria-haspopup="dialog"
        disabled={triggerDisabled}
        title={triggerDisabled ? "Chargement de votre profil utilisateur" : "Personnaliser l’intensité des couleurs Dispatch"}
        onClick={() => setIsOpen((currentValue) => !currentValue)}
      >
        <Palette aria-hidden="true" className="h-3.5 w-3.5" strokeWidth={2.1} />
        <span className="whitespace-nowrap">Affichage</span>
      </button>

      {isOpen && getReactPortalRoot() instanceof HTMLElement ? createPortal(
        <div
          id={popoverId}
          ref={refs.setFloating}
          role="dialog"
          aria-labelledby={`${popoverId}-title`}
          className="z-popover grid w-[min(19rem,calc(100vw-1rem))] gap-3 rounded-[4px] border border-[rgba(148,163,184,0.2)] bg-white p-3.5 text-left shadow-[0_8px_24px_rgba(15,23,42,0.12)] motion-safe:animate-popover-enter"
          data-dispatch-color-intensity-popover="true"
          style={{
            ...floatingStyles,
            visibility: isPositioned ? "visible" : "hidden",
          }}
          onKeyDown={handlePopoverKeyDown}
        >
          <div className="flex items-start justify-between gap-3">
            <div className="grid min-w-0 gap-0.5">
              <div id={`${popoverId}-title`} className="text-[0.78rem] font-extrabold text-slate-900">
                Intensité des couleurs
              </div>
              <p className="text-[0.67rem] leading-4 text-slate-500">
                L’aperçu s’applique immédiatement à la palette Dispatch.
              </p>
            </div>
            <span
              className="inline-flex min-w-[3.4rem] justify-center rounded-[4px] border border-[var(--color-border-success-subtle)] bg-accent-soft px-2 py-1 text-[0.72rem] font-black tabular-nums text-[var(--color-shell-action-soft-ink)]"
              aria-live="polite"
            >
              {formattedValue}
            </span>
          </div>

          <div className="grid gap-1.5">
            <label htmlFor={sliderId} className="sr-only">
              Intensité des couleurs Dispatch
            </label>
            <input
              id={sliderId}
              ref={sliderRef}
              type="range"
              min={DISPATCH_COLOR_INTENSITY_MIN}
              max={DISPATCH_COLOR_INTENSITY_MAX}
              step="1"
              value={dispatchColorIntensityPercent}
              aria-valuetext={`Intensité ${formattedValue}`}
              className="h-2 w-full cursor-pointer accent-[var(--color-accent)] focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2"
              data-dispatch-color-intensity-slider="true"
              onChange={handleSliderChange}
            />
            <div className="flex items-center justify-between text-[0.62rem] font-bold tabular-nums text-slate-400" aria-hidden="true">
              <span>{formatSignedPercent(DISPATCH_COLOR_INTENSITY_MIN)}</span>
              <span>Défaut · 0 %</span>
              <span>{formatSignedPercent(DISPATCH_COLOR_INTENSITY_MAX)}</span>
            </div>
          </div>

          <div className="flex items-center justify-between gap-3 border-t border-slate-100 pt-2.5">
            <span
              className={joinClasses([
                "inline-flex min-w-0 items-center gap-1.5 text-[0.64rem] font-semibold",
                errorMessage ? "text-rose-600" : "text-slate-500",
              ])}
              role="status"
            >
              {isSaving ? <LoaderCircle aria-hidden="true" className="h-3 w-3 animate-spin" /> : null}
              {errorMessage ? "Synchronisation indisponible" : statusLabel}
            </span>
            <button
              type="button"
              className="inline-flex h-7 items-center justify-center gap-1.5 rounded-[4px] border border-slate-200 bg-white px-2 text-[0.66rem] font-extrabold text-slate-600 transition-colors hover:bg-slate-100 hover:text-slate-950 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 disabled:cursor-default disabled:opacity-50"
              disabled={dispatchColorIntensityPercent === DEFAULT_DISPATCH_COLOR_INTENSITY_PERCENT && !hasPendingChanges}
              onClick={handleReset}
            >
              <RotateCcw aria-hidden="true" className="h-3 w-3" />
              Réinitialiser
            </button>
          </div>
        </div>,
        getReactPortalRoot(),
      ) : null}
    </div>
  );
}
